import { lineStyleFromSettings } from "./overlay-utils";
import type { Point2D } from "./drawing-math";

export const UI_FONT_FAMILY = "Inter, Roboto, ui-sans-serif, system-ui, sans-serif";
export const DATA_FONT_FAMILY = "Roboto, ui-monospace, SFMono-Regular, Menlo, monospace";


export function lineFigure(
  coordinates: Point2D[],
  style: Record<string, unknown> | undefined,
  colorKey = "color"
) {
  const ls = lineStyleFromSettings({
    color: (style?.[colorKey] as string) || style?.color,
    lineWidth: style?.lineWidth,
    lineStyle: style?.lineStyle,
  });
  return {
    type: "line" as const,
    attrs: { coordinates },
    styles: {
      style: ls.style,
      color: ls.color,
      size: ls.size,
      dashedValue: ls.dashedValue ?? [0, 0],
    },
  };
}

export function horizontalLineFigure(
  y: number,
  x1: number,
  x2: number,
  style: Record<string, unknown> | undefined,
  colorKey = "lineColor"
) {
  return lineFigure(
    [
      { x: Math.min(x1, x2), y },
      { x: Math.max(x1, x2), y },
    ],
    style,
    colorKey
  );
}

export function textLabelFigure(
  x: number,
  y: number,
  text: string,
  style: Record<string, unknown> | undefined,
  colorKey = "textColor"
) {
  return {
    type: "text" as const,
    attrs: {
      x,
      y,
      text,
      align: "left",
      baseline: "middle",
    },
    styles: {
      style: "fill",
      color: (style?.[colorKey] as string) || style?.textColor || "#666666",
      size: (style?.fontSize as number) || 12,
      family: (style?.fontFamily as string) || "Arial",
      weight: (style?.fontWeight as string) || "normal",
    },
  };
}

/* ────────────────────────────────────────────────────────────────────────────
   Chip labels — ONE implementation shared by text-label, price-label, Elliott
   markers and the Fibonacci level tags.

   The colour a user picks must drive the WHOLE chip, not just its border. The
   precedence chain below is deliberate: older drawings were saved with only
   `color` (or only `borderColor`) set, so resolving through the chain means
   they now paint fully without rewriting any persisted data.
   ──────────────────────────────────────────────────────────────────────────── */

export const CHIP_FALLBACK_COLOR = "#2196F3";

export function resolveChipColor(
  style: Record<string, unknown> | undefined,
  fallback: string = CHIP_FALLBACK_COLOR
): string {
  const chain = [style?.backgroundColor, style?.color, style?.borderColor];
  for (const c of chain) {
    if (typeof c === "string" && c.length > 0) return c;
  }
  return fallback;
}

/** Parse #rgb / #rrggbb / rgb() / rgba() into 0-255 channels. Null if unparseable. */
export function parseColorChannels(
  color: string | undefined
): { r: number; g: number; b: number; a: number } | null {
  if (!color) return null;
  const c = color.trim().toLowerCase();
  if (c === "transparent") return { r: 0, g: 0, b: 0, a: 0 };
  if (c.startsWith("#")) {
    const hex = c.slice(1);
    if (hex.length === 3) {
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16),
        a: 1,
      };
    }
    if (hex.length === 6 || hex.length === 8) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
        a: hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1,
      };
    }
    return null;
  }
  const m = c.match(/^rgba?\(([^)]+)\)$/);
  if (!m) return null;
  const parts = m[1].split(",").map((p) => parseFloat(p.trim()));
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null;
  return { r: parts[0], g: parts[1], b: parts[2], a: parts.length > 3 ? parts[3] : 1 };
}

/** WCAG relative luminance (0 = black, 1 = white). */
export function relativeLuminance(color: string | undefined): number {
  const ch = parseColorChannels(color);
  if (!ch) return 0;
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(ch.r) + 0.7152 * lin(ch.g) + 0.0722 * lin(ch.b);
}

/**
 * Pick the caption colour that contrasts best with `background`.
 * 0.179 is the exact WCAG crossover where contrast-vs-white equals
 * contrast-vs-black — above it dark ink wins, below it light ink wins.
 */
export function inkFor(background: string | undefined): string {
  return relativeLuminance(background) > 0.179 ? "#0A0A0A" : "#FAFAFA";
}

export function withAlpha(color: string | undefined, alpha: number): string {
  const ch = parseColorChannels(color);
  if (!ch) return `rgba(33, 150, 243, ${alpha})`;
  return `rgba(${Math.round(ch.r)}, ${Math.round(ch.g)}, ${Math.round(ch.b)}, ${alpha})`;
}

export interface ChipOptions {
  align?: "left" | "center" | "right";
  /** Vertical anchor of the chip relative to y. */
  baseline?: "top" | "middle" | "bottom";
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: string;
  paddingX?: number;
  paddingY?: number;
  borderRadius?: number;
  /** Explicit fill; defaults to resolveChipColor(style). */
  color?: string;
  /** Explicit border; defaults to the fill (so the chip reads as one colour). */
  borderColor?: string;
  /** Explicit caption colour; defaults to style.textColor, else auto-ink. */
  textColor?: string;
}

/**
 * A filled, rounded label chip.
 *
 * IMPORTANT: this is ONE `text` figure, not a rect + text pair. KLineCharts'
 * text figure paints its own background, and the chart-wide default for
 * `overlay.text.backgroundColor` is its brand blue `#1677FF` (4px padding,
 * `#1677FF` border). A text figure that does not set `backgroundColor`
 * therefore renders a BLUE pill — which, drawn over a separately-emitted
 * coloured rect, is what made labels look "blue with coloured edges".
 *
 * Setting the background here means the chip is a single self-measured figure:
 * no width estimation, and no default colour can leak through.
 */
export function chipFigure(
  x: number,
  y: number,
  text: string,
  style: Record<string, unknown> | undefined,
  opts: ChipOptions = {}
) {
  const fontSize = opts.fontSize ?? (style?.fontSize as number) ?? 12;
  const fontFamily = opts.fontFamily ?? (style?.fontFamily as string) ?? UI_FONT_FAMILY;
  const fontWeight = opts.fontWeight ?? (style?.fontWeight as string) ?? "normal";
  const paddingX = opts.paddingX ?? 6;
  const paddingY = opts.paddingY ?? 3;

  const fill = opts.color ?? resolveChipColor(style);
  // An explicit textColor wins; otherwise contrast against whatever we filled with.
  const ink =
    opts.textColor ??
    (typeof style?.textColor === "string" && style.textColor
      ? (style.textColor as string)
      : inkFor(fill));

  return [
    {
      type: "text" as const,
      attrs: {
        x,
        y,
        text,
        align: opts.align ?? "center",
        baseline: opts.baseline ?? "middle",
      },
      styles: {
        style: "fill",
        color: ink,
        size: fontSize,
        family: fontFamily,
        weight: fontWeight,
        // Every one of these must be explicit — anything omitted falls back to
        // the chart's default overlay text style, which is blue.
        backgroundColor: fill,
        borderColor: opts.borderColor ?? (style?.borderColor as string) ?? fill,
        borderSize: 1,
        borderStyle: "solid",
        borderRadius: opts.borderRadius ?? 4,
        paddingLeft: paddingX,
        paddingRight: paddingX,
        paddingTop: paddingY,
        paddingBottom: paddingY,
      },
    },
  ];
}

/** Translucent band spanning [x1,x2] × [y1,y2] — the Fibonacci zone fill. */
export function bandFigure(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  color: string | undefined,
  alpha = 0.07
) {
  const left = Math.min(x1, x2);
  const top = Math.min(y1, y2);
  return {
    type: "rect" as const,
    attrs: {
      x: left,
      y: top,
      width: Math.abs(x2 - x1),
      height: Math.abs(y2 - y1),
    },
    styles: {
      style: "fill",
      color: withAlpha(color, alpha),
      borderSize: 0,
    },
  };
}

/** Alternating zone fills between consecutive Fibonacci levels. */
export function fibBandFigures(
  levels: Array<{ y: number }>,
  x1: number,
  x2: number,
  color: string | undefined,
  minGapPx = 6
) {
  const figures: ReturnType<typeof bandFigure>[] = [];
  for (let i = 0; i < levels.length - 1; i++) {
    if (i % 2 !== 0) continue; // alternate, so adjacent zones stay distinguishable
    const a = levels[i].y;
    const b = levels[i + 1].y;
    if (Math.abs(b - a) < minGapPx) continue; // skip hairline-thin zones
    figures.push(bandFigure(x1, a, x2, b, color, 0.07));
  }
  return figures;
}

export function readOverlaySettings(overlay: { extendData?: { settings?: Record<string, unknown> } } | undefined) {
  const settings = overlay?.extendData?.settings || {};
  const style = (settings.style as Record<string, unknown>) || {};
  const levels = settings.levels as Array<{ value: number; visible: boolean }> | undefined;
  const computed = settings.computed as Record<string, unknown> | undefined;
  const angle =
    typeof settings.angle === "number"
      ? settings.angle
      : typeof (settings.values as Record<string, unknown>)?.Angle === "number"
        ? ((settings.values as Record<string, unknown>).Angle as number)
        : 45;
  const divisions =
    typeof (settings.values as Record<string, unknown>)?.Divisions === "number"
      ? ((settings.values as Record<string, unknown>).Divisions as number)
      : 8;
  // Fibonacci presentation flags. `extendRight` is opt-in: a study is bounded to
  // its own [t0,t1] unless the user explicitly asks it to project forward.
  const extendRight = style.extendRight === true || settings.extendRight === true;
  const showBands = style.showBands !== false;
  const showPrices = style.showPrices !== false;
  const anchorEmphasis = style.anchorEmphasis !== false;

  return {
    settings,
    style,
    levels,
    computed,
    angle,
    divisions,
    extendRight,
    showBands,
    showPrices,
    anchorEmphasis,
  };
}
